import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

interface ProcessingStatus {
  queue_item_id: string;
  status: 'pending' | 'processing' | 'completed' | 'error' | 'review_required';
  progress_percentage: number;
  current_stage: string;
  error_message?: string;
  estimated_completion?: string;
  processing_time_ms?: number;
}

interface QueueUpdate {
  id: string;
  status?: string;
  processing_status: string;
  extracted_data?: any;
  error_message?: string;
  processing_time_ms?: number;
}

export const useRealtimeStatus = (queueItemId: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  const channelRef = useRef<any>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusHistoryRef = useRef<ProcessingStatus[]>([]);

  // Initialize real-time connection
  const initializeConnection = () => {
    if (!user?.id || !queueItemId) return;

    console.log(`🔄 Initializing real-time status for queue item: ${queueItemId}`);

    const channel = supabase
      .channel(`queue-status-${queueItemId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bill_upload_queue',
          filter: `id=eq.${queueItemId}`
        },
        (payload) => {
          console.log('📥 Queue item update received:', payload);
          handleQueueUpdate(payload.new as QueueUpdate);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'processing_logs',
          filter: `queue_item_id=eq.${queueItemId}`
        },
        (payload) => {
          console.log('📊 Processing log update:', payload);
          handleProcessingLogUpdate(payload);
        }
      )
      .subscribe((status) => {
        console.log(`🔗 Subscription status: ${status}`);
        setIsConnected(status === 'SUBSCRIBED');
        
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          scheduleReconnect();
        }
      });

    channelRef.current = channel;
  };

  // Handle queue item updates
  const handleQueueUpdate = (update: any) => {
    const newStatus: ProcessingStatus = {
      queue_item_id: update.id,
      status: update.processing_status as ProcessingStatus['status'],
      progress_percentage: getProgressFromStatus(update.processing_status),
      current_stage: getStageFromStatus(update.processing_status),
      error_message: update.error_message,
      processing_time_ms: update.processing_time_ms
    };

    setProcessingStatus(newStatus);
    setLastUpdate(new Date());
    
    // Add to history
    statusHistoryRef.current.push(newStatus);
    
    // Keep only last 10 status updates
    if (statusHistoryRef.current.length > 10) {
      statusHistoryRef.current = statusHistoryRef.current.slice(-10);
    }

    // Show toast notifications for important status changes
    showStatusNotification(newStatus);
  };

  // Handle processing log updates
  const handleProcessingLogUpdate = (payload: any) => {
    if (payload.eventType === 'INSERT' && payload.new) {
      const log = payload.new;
      
      if (log.log_level === 'error') {
        toast({
          title: "Processing Error",
          description: log.message || "An error occurred during processing",
          variant: "destructive"
        });
      }
      
      // Update current stage if provided in log
      if (log.metadata?.stage) {
        setProcessingStatus(prev => prev ? {
          ...prev,
          current_stage: log.metadata.stage
        } : null);
      }
    }
  };

  // Get progress percentage from status
  const getProgressFromStatus = (status: string): number => {
    switch (status) {
      case 'pending': return 0;
      case 'processing': return 50;
      case 'completed': return 100;
      case 'review_required': return 90;
      case 'error': return 0;
      default: return 0;
    }
  };

  // Get human-readable stage from status
  const getStageFromStatus = (status: string): string => {
    switch (status) {
      case 'pending': return 'Waiting in queue';
      case 'processing': return 'Extracting data';
      case 'completed': return 'Processing complete';
      case 'review_required': return 'Requires review';
      case 'error': return 'Processing failed';
      default: return 'Unknown';
    }
  };

  // Show status notification
  const showStatusNotification = (status: ProcessingStatus) => {
    switch (status.status) {
      case 'processing':
        toast({
          title: "Processing Started",
          description: "Your document is being processed",
        });
        break;
      case 'completed':
        toast({
          title: "Processing Complete",
          description: "Your document has been processed successfully",
        });
        break;
      case 'review_required':
        toast({
          title: "Review Required",
          description: "Processing complete but manual review is needed",
          variant: "default"
        });
        break;
      case 'error':
        toast({
          title: "Processing Failed",
          description: status.error_message || "An error occurred during processing",
          variant: "destructive"
        });
        break;
    }
  };

  // Schedule reconnection
  const scheduleReconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      console.log('🔄 Attempting to reconnect...');
      cleanup();
      initializeConnection();
    }, 3000);
  };

  // Cleanup function
  const cleanup = () => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setIsConnected(false);
  };

  // Manual refresh
  const refreshStatus = async () => {
    if (!queueItemId) return;

    try {
      const { data, error } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('id', queueItemId)
        .single();

      if (error) throw error;

      if (data) {
        handleQueueUpdate(data);
      }
    } catch (error) {
      console.error('Failed to refresh status:', error);
      toast({
        title: "Refresh Failed",
        description: "Could not refresh processing status",
        variant: "destructive"
      });
    }
  };

  // Initialize connection on mount
  useEffect(() => {
    initializeConnection();
    return cleanup;
  }, [queueItemId, user?.id]);

  // Auto-refresh on connection loss
  useEffect(() => {
    if (!isConnected && queueItemId) {
      const interval = setInterval(refreshStatus, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [isConnected, queueItemId]);

  return {
    processingStatus,
    isConnected,
    lastUpdate,
    statusHistory: statusHistoryRef.current,
    refreshStatus,
    cleanup
  };
};
